/**
 * brandProfileSchema.js — the single source of truth for the AI Brand Profile.
 *
 * Every prompt field that declares `brandKey: "<key>"` is auto-filled from the matching key here
 * when the user clicks "Use my brand profile". Keys MUST match the Pydantic BrandProfile model
 * in backend/models/ai_lab.py.
 */
export const BRAND_PROFILE_SECTIONS = [
  {
    id: "basics",
    title: "Brand basics",
    description: "The essentials every prompt needs.",
    fields: [
      { key: "brandName", label: "Brand name", type: "text", placeholder: "e.g. Rezerrection Energy", required: true },
      { key: "category", label: "Product category", type: "text", placeholder: "e.g. Energy drink, Beef jerky, Protein bar", required: true },
      { key: "productDescription", label: "Product description", type: "textarea", placeholder: "What is it, what's in it, what format/size does it come in?", required: true },
      { key: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"] },
      { key: "website", label: "Website", type: "text", placeholder: "https://" },
    ],
  },
  {
    id: "positioning",
    title: "Positioning & consumer",
    description: "Who it's for and why they should care.",
    fields: [
      { key: "positioning", label: "Brand positioning", type: "textarea", placeholder: "One or two sentences. e.g. Natural premium performance energy without the aggressive energy-drink aesthetic." },
      { key: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "e.g. Men and women 18–35 interested in fitness, wellness and active lifestyles" },
      { key: "claims", label: "Key claims & differentiators", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors" },
      { key: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine, Electrolytes" },
      { key: "competitors", label: "Main competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost" },
      { key: "pricePoint", label: "Retail price point", type: "text", placeholder: "e.g. $2.99 per can, $4.49 per bag" },
      { key: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery, Convenience" },
    ],
  },
  {
    id: "personality",
    title: "Personality & voice",
    description: "How the brand looks, sounds and behaves.",
    fields: [
      { key: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic, Approachable" },
      { key: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "e.g. Direct and encouraging. Short sentences. No hype words. Talks like a knowledgeable training partner." },
      { key: "visualStyle", label: "Visual style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold" },
      { key: "avoid", label: "What AI should avoid", type: "textarea", placeholder: "e.g. Looking like a sports supplement; excessive neon colors; gaming imagery" },
      { key: "founderStory", label: "Founder story (short)", type: "textarea", placeholder: "2–4 sentences on why you started the brand." },
    ],
  },
  {
    id: "visual",
    title: "Visual identity",
    description: "Used for design, packaging, photography and video prompts.",
    fields: [
      { key: "brandColors", label: "Brand colors", type: "tags", placeholder: "#0B0B0B, #F4F1EA, #FF5A1F  (or names: Charcoal, Bone, Flame orange)" },
      { key: "fonts", label: "Fonts", type: "tags", placeholder: "Inter, Söhne, Playfair Display" },
      { key: "logoUrl", label: "Logo image URL", type: "url", placeholder: "https://…/logo.png" },
      { key: "packagingImages", label: "Packaging image URLs", type: "urls", placeholder: "One URL per line" },
    ],
  },
];

export const BRAND_PROFILE_KEYS = BRAND_PROFILE_SECTIONS.flatMap((s) => s.fields.map((f) => f.key));

export const EMPTY_BRAND_PROFILE = Object.fromEntries(BRAND_PROFILE_KEYS.map((k) => [k, ""]));

/** 0–100 completeness score used on the dashboard banner. */
export function brandProfileCompleteness(profile) {
  if (!profile) return 0;
  const weighted = [
    ["brandName", 3], ["category", 3], ["productDescription", 3], ["positioning", 3], ["targetConsumer", 3],
    ["claims", 2], ["competitors", 2], ["personality", 2], ["brandVoice", 2], ["visualStyle", 2], ["avoid", 2],
    ["ingredients", 1], ["pricePoint", 1], ["channels", 1], ["brandColors", 1], ["fonts", 1], ["founderStory", 1], ["stage", 1], ["website", 1], ["logoUrl", 1], ["packagingImages", 1],
  ];
  const total = weighted.reduce((s, [, w]) => s + w, 0);
  const got = weighted.reduce((s, [k, w]) => s + (profile[k] && String(profile[k]).trim() ? w : 0), 0);
  return Math.round((got / total) * 100);
}
